import { getIconByName } from '../../utils/iconUtils'
import { useChatAppearance } from '../../core/client/useChatAppearance'
import { ChatWordmark, SpeechMark } from './ChatIdentity'
import { Box, Button, Flex, Image, Text, useColorModeValue } from '@chakra-ui/react'
import React from 'react'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { Agent, MAIN_CHAT_AGENT_SLUG } from '../../types/agentTypes'
import { KnowledgeSource } from '../../types/chatTypes'
import MarkdownContent from '../MarkdownContent'

interface EmptyStateMessageProps {
  agent: Agent
  onQuestionClick?: (question: string, searchKnowledgeSource: KnowledgeSource) => void
}

export const AgentWatermark: React.FC<EmptyStateMessageProps> = ({ agent, onQuestionClick }) => {
  const { welcomeMark, welcomeLogoHeight, branding, logoUrl, brandLogoUrl } = useChatAppearance()
  const { t } = useLanguage()
  // By slug too: without an assistantName the main chat is named "<company> Chat".
  const showBrandLogo =
    welcomeMark === 'logo' && (agent.slug === MAIN_CHAT_AGENT_SLUG || agent.name === branding?.assistantName)
  // The brand logo stands in for the assistant's name: the chat wordmark if set, else the company logo.
  const logoIsName = showBrandLogo && !!(logoUrl || brandLogoUrl)
  const agentIcon = showBrandLogo
    ? null
    : getIconByName(agent.profile_picture_url, 28) ?? (agent.type === 'normal' ? getIconByName('Bot', 28) : null)
  const descriptionColor = useColorModeValue('gray.600', 'gray.300')
  const description = (agent.name === 'AVA' ? t('fallbackAgentDescription') : agent.description || '').trim()

  const handleQuestionClick = (question: string) => {
    if (onQuestionClick) {
      const searchKnowledgeSource = 'LLM_ONLY'
      onQuestionClick(question, searchKnowledgeSource)
    }
  }

  return (
    <Flex
      direction='column'
      align='center'
      justify='center'
      minH='100%'
      h='auto'
      py={10}
      w='100%'
      textAlign='center'
    >
      {welcomeMark === 'speech' && (
        <Box mb={6}>
          <SpeechMark />
        </Box>
      )}
      {logoIsName &&
        (logoUrl ? (
          <Box mb={3}>
            <ChatWordmark size={welcomeLogoHeight} />
          </Box>
        ) : (
          <Image src={brandLogoUrl} alt={branding?.assistantName} h={16} mb={3} objectFit='contain' />
        ))}
      {agentIcon && (
        <Box mb={3} p={3} borderRadius='chat.md' bg='chat.iconSurface' color='chat.iconInk'>
          {agentIcon}
        </Box>
      )}
      <Box pb={0} mb={0}>
        {!logoIsName && (
          <Text fontSize={['2xl', '3xl']} fontWeight='bold' letterSpacing='-0.7px'>
            {agent.name}
          </Text>
        )}
        {agent.job_category?.name && (
          <Text fontSize='sm' color='gray.400' fontWeight='normal' borderRadius='md' display='inline-block'>
            {agent.job_category.name}
          </Text>
        )}
      </Box>

      {description && (
        <MarkdownContent
          content={description}
          fontSize='md'
          color={descriptionColor}
          mt={0}
          maxW='600px'
          mx='auto'
          // Prose stays centered like before; lists and tables read badly
          // centered, so left-align those and keep them inside the column.
          sx={{
            '& ul, & ol, & table': { textAlign: 'left', width: 'fit-content', mx: 'auto' },
          }}
        />
      )}

      {/* Example Questions */}
      {agent.example_questions && agent.example_questions.length > 0 && (
        <Box mt={5} maxW='600px' mx='auto'>
          <Box
            display='grid'
            gap={2}
            sx={{
              gridTemplateColumns: {
                base: '1fr',
                md: `repeat(${Math.min(agent.example_questions.length, 2)}, 1fr)`,
              },
            }}
          >
            {agent.example_questions.map((question, idx) => (
              <Button
                key={idx}
                variant='ghost'
                border='1px solid'
                borderColor='chat.border'
                borderRadius='chat.md'
                h='auto'
                minH={12}
                px={4}
                py={3}
                color='chat.muted'
                fontSize='sm'
                fontWeight='normal'
                whiteSpace='normal'
                textAlign='left'
                justifyContent='flex-start'
                onClick={() => handleQuestionClick(question)}
                _focusVisible={{ outline: '2px solid', outlineColor: 'chat.focus', outlineOffset: '2px' }}
              >
                {question}
              </Button>
            ))}
          </Box>
        </Box>
      )}
    </Flex>
  )
}
